import React, { useState, useEffect } from 'react';
import { X, Share, Plus, Smartphone } from 'lucide-react';
import { useIOSSafari, usePWAMode } from '../hooks/useMobileOptimization';

/**
 * iOS Safari Install Prompt
 * Shows a prompt for iOS users to add the app to their home screen
 */
const IOSInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const isIOSSafari = useIOSSafari();
  const isPWA = usePWAMode();

  useEffect(() => {
    // Only show for iOS Safari users who haven't installed the PWA
    if (isIOSSafari && !isPWA) {
      // Check if user has dismissed the prompt before
      const dismissed = localStorage.getItem('ios-install-prompt-dismissed');
      const lastDismissed = dismissed ? new Date(dismissed) : null;
      const daysSinceDismissed = lastDismissed 
        ? (new Date().getTime() - lastDismissed.getTime()) / (1000 * 60 * 60 * 24)
        : null;

      // Show again after 7 days
      if (!dismissed || (daysSinceDismissed && daysSinceDismissed > 7)) {
        // Delay showing the prompt
        const timer = setTimeout(() => setShowPrompt(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [isIOSSafari, isPWA]);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('ios-install-prompt-dismissed', new Date().toISOString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 pb-safe" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      <div className="max-w-md mx-auto bg-[#1A1A1A] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-black" />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Add to Home Screen</p>
              <p className="text-xs text-white/50">Get the full app experience</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-white/50" />
          </button>
        </div>

        {/* Instructions */}
        <div className="p-4 space-y-4">
          <p className="text-sm text-white/70">
            Install LEDWORLD for quick access and a better browsing experience:
          </p>
          
          <div className="space-y-3">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-400">1</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/70">Tap the</span>
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-white/10">
                  <Share className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-medium text-white/70">Share</span>
                </div>
                <span className="text-sm text-white/70">button</span>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-400">2</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-white/70">Select</span>
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-white/10">
                  <Plus className="w-4 h-4 text-white/70" />
                  <span className="text-xs font-medium text-white/70">Add to Home Screen</span>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-blue-400">3</span>
              </div>
              <span className="text-sm text-white/70">Tap "Add" to install</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <button
            onClick={handleDismiss}
            className="w-full py-3 bg-white/5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default IOSInstallPrompt;
